import { useEffect } from "react";
import { Navigate, Outlet, useNavigate, useOutletContext } from "react-router";

import type { SourceResource } from "@prequel/import-react";
import { useSource } from "@prequel/import-react";
import { Button, ButtonStyle, PageHeading, Spinner } from "@prequel/shared-components";

import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type SourceContext = {
  source: SourceResource;
  refreshSource: () => void;
};

export function useSourceContext(): SourceContext {
  return useOutletContext<SourceContext>();
}

const SourcePage = () => {
  const sourceID = useRequiredParam("sourceID");
  const { addToast } = useToast();
  const { data: source, error, isLoading, refetch: refreshSource } = useSource(sourceID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load source." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!source) {
    return <Navigate to="/import/sources" replace />;
  }

  return <Outlet context={{ source, refreshSource }} />;
};

export const SourceDetailsPage = () => {
  const navigate = useNavigate();
  const { source, refreshSource } = useSourceContext();

  return (
    <div className="max-w-4xl px-7 py-4">
      <PageHeading
        title={source.name}
        onGoBack={() => navigate("/import/sources")}
        breadcrumbs={<Breadcrumbs dynamicLabel={source.name} />}
      >
        <Button
          text="Edit"
          onClick={() => navigate(`/import/sources/${source.id}/edit`)}
          type={ButtonStyle.SECONDARY}
        />
      </PageHeading>
      <div className="flex-1">
        <div className="relative mx-auto max-w-4xl">
          <div className="pt-0 pb-16">
            <Outlet context={{ source, refreshSource }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourcePage;
